import { API_BASE } from '../env'
import { request } from './http'
import type { Refine, Schema } from './schema'

// ── Alerts: incidents ────────────────────────────────────────────────────────────────────────────
//
// An incident is something that needs attention — a device offline, a store down, a printer out of
// paper — from when the relay noticed until it was resolved. The relay opens and resolves most of
// them itself; people acknowledge, snooze and resolve them. A caller sees the incidents where they
// may see devices (Plans/mobile-app.md, Phase A).

export type IncidentKind =
  | 'device_offline'
  | 'location_down'
  | 'printer_condition'
  | 'telemetry_threshold'
  | 'ml_alert'
  | 'rollout'
  | 'security'

export type IncidentState = 'triggered' | 'acknowledged' | 'resolved'
export type Urgency = 'high' | 'low'
export type IncidentAction = 'acknowledge' | 'snooze' | 'unsnooze' | 'resolve'

export type IncidentWire = Refine<
  Schema<'IncidentWire'>,
  {
    kind: IncidentKind
    urgency: Urgency
    state: IncidentState
    detail?: Record<string, unknown> | null
  }
>

export type IncidentPageWire = Refine<Schema<'IncidentPageWire'>, { incidents: IncidentWire[] }>
export type IncidentSummaryWire = Schema<'IncidentSummaryWire'>
export type IncidentEventWire = Refine<Schema<'IncidentEventWire'>, { detail?: Record<string, unknown> | null }>
export type IncidentDeliveryWire = Schema<'IncidentDeliveryWire'>
export type IncidentDetailWire = Refine<
  Schema<'IncidentDetailWire'>,
  { incident: IncidentWire; events: IncidentEventWire[]; deliveries: IncidentDeliveryWire[] }
>

export interface IncidentQuery {
  /** `open` (the default) is triggered and acknowledged; `all` includes resolved. */
  state?: 'open' | 'all' | IncidentState
  urgency?: Urgency
  kinds?: IncidentKind[]
  brandId?: string
  locationId?: string
  deviceId?: string
  parentId?: string
  cursor?: string
  limit?: number
}

export function getIncidents(query: IncidentQuery = {}, signal?: AbortSignal): Promise<IncidentPageWire> {
  const params = new URLSearchParams()
  if (query.state) params.set('state', query.state)
  if (query.urgency) params.set('urgency', query.urgency)
  if (query.kinds && query.kinds.length > 0) params.set('kind', query.kinds.join(','))
  if (query.brandId) params.set('brand_id', query.brandId)
  if (query.locationId) params.set('location_id', query.locationId)
  if (query.deviceId) params.set('device_id', query.deviceId)
  if (query.parentId) params.set('parent_id', query.parentId)
  if (query.cursor) params.set('cursor', query.cursor)
  if (query.limit != null) params.set('limit', String(query.limit))
  return request<IncidentPageWire>('GET', `${API_BASE}/incidents?${params.toString()}`, { signal })
}

export function getIncidentSummary(): Promise<IncidentSummaryWire> {
  return request<IncidentSummaryWire>('GET', `${API_BASE}/incidents/summary`)
}

export function getIncident(incidentId: string): Promise<IncidentDetailWire> {
  return request<IncidentDetailWire>('GET', `${API_BASE}/incidents/${incidentId}`)
}

export interface IncidentActionOptions {
  note?: string
  /** For a snooze: how long. */
  minutes?: number
}

/** Every action is sent from the portal (`client: 'portal'`), which the incident's history records. */
export function acknowledgeIncident(incidentId: string, options: IncidentActionOptions = {}): Promise<IncidentWire> {
  return request<IncidentWire>('POST', `${API_BASE}/incidents/${incidentId}/acknowledge`, {
    json: { client: 'portal', note: options.note },
  })
}

export function snoozeIncident(incidentId: string, minutes: number): Promise<IncidentWire> {
  return request<IncidentWire>('POST', `${API_BASE}/incidents/${incidentId}/snooze`, {
    json: { client: 'portal', minutes },
  })
}

export function unsnoozeIncident(incidentId: string): Promise<IncidentWire> {
  return request<IncidentWire>('POST', `${API_BASE}/incidents/${incidentId}/unsnooze`, {
    json: { client: 'portal' },
  })
}

export function resolveIncident(incidentId: string, options: IncidentActionOptions = {}): Promise<IncidentWire> {
  return request<IncidentWire>('POST', `${API_BASE}/incidents/${incidentId}/resolve`, {
    json: { client: 'portal', note: options.note },
  })
}

export type IncidentBulkWire = Schema<'IncidentBulkWire'>

export function bulkIncidents(
  ids: string[],
  action: IncidentAction,
  options: IncidentActionOptions = {},
): Promise<IncidentBulkWire> {
  return request<IncidentBulkWire>('POST', `${API_BASE}/incidents/bulk`, {
    json: { ids, action, client: 'portal', minutes: options.minutes, note: options.note },
  })
}

/**
 * The live stream of incident changes (Server-Sent Events), as the response body. The caller reads
 * it with {@link readIncidentEvents} and cancels it with `signal`; it never ends by itself.
 */
export function openIncidentStream(signal: AbortSignal): Promise<ReadableStream<Uint8Array> | null> {
  return request<ReadableStream<Uint8Array> | null>('GET', `${API_BASE}/events/stream`, {
    accept: 'stream',
    signal,
    timeoutMs: null,
  })
}

/**
 * The incidents in a Server-Sent Events body, as they arrive. Comments (keep-alives) and fields
 * other than `event` and `data` are skipped; an event that is not JSON is dropped.
 */
export async function* readIncidentEvents(stream: ReadableStream<Uint8Array>): AsyncGenerator<IncidentWire> {
  const reader = stream.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  for (;;) {
    const { value, done } = await reader.read()
    if (done) return
    buffer += decoder.decode(value, { stream: true })
    let end: number
    while ((end = buffer.indexOf('\n\n')) >= 0) {
      const block = buffer.slice(0, end)
      buffer = buffer.slice(end + 2)
      let event = 'message'
      const data: string[] = []
      for (const line of block.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim()
        else if (line.startsWith('data:')) data.push(line.slice(5).trimStart())
      }
      if (event !== 'incident' || data.length === 0) continue
      try {
        yield JSON.parse(data.join('\n')) as IncidentWire
      } catch {
        // Not an incident: skipped.
      }
    }
  }
}

// ── My notifications ─────────────────────────────────────────────────────────────────────────────

export type AlertSubscriptionWire = Refine<
  Schema<'AlertSubscriptionWire'>,
  { kind: IncidentKind | '*'; min_urgency: Urgency }
>

export function getMySubscriptions(): Promise<{ subscriptions: AlertSubscriptionWire[] }> {
  return request<{ subscriptions: AlertSubscriptionWire[] }>('GET', `${API_BASE}/me/alert-subscriptions`)
}

export function setMySubscriptions(subscriptions: AlertSubscriptionWire[]): Promise<{ subscriptions: AlertSubscriptionWire[] }> {
  return request<{ subscriptions: AlertSubscriptionWire[] }>('PUT', `${API_BASE}/me/alert-subscriptions`, {
    json: { subscriptions },
  })
}

export type NotificationSettingsWire = Schema<'NotificationSettingsWire'>

export function getMyNotificationSettings(): Promise<NotificationSettingsWire> {
  return request<NotificationSettingsWire>('GET', `${API_BASE}/me/notification-settings`)
}

export function setMyNotificationSettings(settings: NotificationSettingsWire): Promise<NotificationSettingsWire> {
  return request<NotificationSettingsWire>('PUT', `${API_BASE}/me/notification-settings`, { json: settings })
}

export function sendTestNotification(channel?: 'email' | 'push'): Promise<{ queued: number }> {
  return request<{ queued: number }>('POST', `${API_BASE}/me/notification-test`, { json: { channel } })
}

/** What the "Pair a phone" QR code carries: this relay's portal address, and its certificate's pin when self-signed. */
export type MobilePairingWire = Schema<'MobilePairingWire'>

export function getPhonePairing(): Promise<MobilePairingWire> {
  return request<MobilePairingWire>('GET', `${API_BASE}/mobile/pairing`)
}

// ── Alerting administration ──────────────────────────────────────────────────────────────────────

export type AlertRuleWire = Refine<
  Schema<'AlertRuleWire'>,
  { kind: IncidentKind; urgency: Urgency; params: Record<string, unknown> }
>

export interface AlertRuleBody {
  kind?: IncidentKind
  name?: string
  brand_id?: string | null
  enabled?: boolean
  urgency?: Urgency
  for_seconds?: number
  params?: Record<string, unknown>
}

export function getAlertRules(): Promise<{ rules: AlertRuleWire[] }> {
  return request<{ rules: AlertRuleWire[] }>('GET', `${API_BASE}/alert-rules`)
}

export function getAlertMetrics(): Promise<{ metrics: Schema<'AlertMetricWire'>[] }> {
  return request<{ metrics: Schema<'AlertMetricWire'>[] }>('GET', `${API_BASE}/alert-rules/metrics`)
}

export function createAlertRule(body: AlertRuleBody): Promise<AlertRuleWire> {
  return request<AlertRuleWire>('POST', `${API_BASE}/alert-rules`, { json: body })
}

export function updateAlertRule(ruleId: string, body: AlertRuleBody): Promise<AlertRuleWire> {
  return request<AlertRuleWire>('PUT', `${API_BASE}/alert-rules/${ruleId}`, { json: body })
}

export function deleteAlertRule(ruleId: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>('DELETE', `${API_BASE}/alert-rules/${ruleId}`)
}

export type EscalationPolicyWire = Refine<Schema<'EscalationPolicyWire'>, { min_urgency: Urgency }>

export interface EscalationPolicyBody {
  name: string
  brand_id?: string | null
  kinds?: IncidentKind[]
  min_urgency?: Urgency
  steps: { after_minutes: number; user_ids: string[]; emails: string[] }[]
  enabled?: boolean
}

export function getEscalationPolicies(): Promise<{ policies: EscalationPolicyWire[] }> {
  return request<{ policies: EscalationPolicyWire[] }>('GET', `${API_BASE}/escalation-policies`)
}

export function createEscalationPolicy(body: EscalationPolicyBody): Promise<EscalationPolicyWire> {
  return request<EscalationPolicyWire>('POST', `${API_BASE}/escalation-policies`, { json: body })
}

export function updateEscalationPolicy(escalationId: string, body: EscalationPolicyBody): Promise<EscalationPolicyWire> {
  return request<EscalationPolicyWire>('PUT', `${API_BASE}/escalation-policies/${escalationId}`, { json: body })
}

export function deleteEscalationPolicy(escalationId: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>('DELETE', `${API_BASE}/escalation-policies/${escalationId}`)
}

export type AlertIntegrationType = 'webhook' | 'pagerduty' | 'slack' | 'teams'

export type AlertIntegrationWire = Refine<
  Schema<'AlertIntegrationWire'>,
  { type: AlertIntegrationType; min_urgency: Urgency; config: Record<string, string> }
>

export interface AlertIntegrationBody {
  name: string
  type?: AlertIntegrationType
  brand_id?: string | null
  kinds?: IncidentKind[]
  min_urgency?: Urgency
  /** Only what changes: a secret left out keeps its value; an empty string removes a setting. */
  config?: Record<string, string>
  enabled?: boolean
}

export function getAlertIntegrations(): Promise<{ integrations: AlertIntegrationWire[] }> {
  return request<{ integrations: AlertIntegrationWire[] }>('GET', `${API_BASE}/alert-integrations`)
}

export function createAlertIntegration(body: AlertIntegrationBody): Promise<AlertIntegrationWire> {
  return request<AlertIntegrationWire>('POST', `${API_BASE}/alert-integrations`, { json: body })
}

export function updateAlertIntegration(integrationId: string, body: AlertIntegrationBody): Promise<AlertIntegrationWire> {
  return request<AlertIntegrationWire>('PUT', `${API_BASE}/alert-integrations/${integrationId}`, { json: body })
}

export function deleteAlertIntegration(integrationId: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>('DELETE', `${API_BASE}/alert-integrations/${integrationId}`)
}

export function testAlertIntegration(integrationId: string): Promise<{ queued: number }> {
  return request<{ queued: number }>('POST', `${API_BASE}/alert-integrations/${integrationId}/test`)
}
